import { isCivilDate } from '@mfw/common';
import type {
  FetchCorpSignedUploadLogoUrlArgs,
  UpdateCorpOrganizationLogoArgs,
  UpdateCorpRebrandThemeArgs,
  FetchCorpOrganizationsArgs,
  CreateCorpOrganizationArgs,
  CorpOrganizationByIdArgs,
  EditCorpOrganizationArgs,
  OffboardCorpMemberArgs,
  FetchCorpMembersArgs,
} from './types/corpOrganizations.types';
import type {
  ApiCorpOrganization,
  ApiCorpMember,
} from '../schemas/corpOrganizations.schema';
import type {
  BaseApiPaginatedResponse,
  BaseApiResponse,
} from './types/api.types';
import type {
  UiSignedUploadUrl,
  ApiSignedUploadUrl,
} from './types/files.types';

import { mapCorpOrganizationFromApi } from './mappers/corpOrganizations.mappers';
import { destroy, patch, post, get, put } from './axios';

function toApiPayload(
  args: Partial<CreateCorpOrganizationArgs>,
): Record<string, unknown> {
  return {
    contactEmail: args.contactEmail ? args.contactEmail : null,
    contactPerson: args.contactPerson,
    contractEndDate: isCivilDate(args.contractEndDate)
      ? args.contractEndDate
      : null,
    contractStartDate: isCivilDate(args.contractStartDate)
      ? args.contractStartDate
      : null,
    name: args.name,
  };
}

export async function fetchCorpOrganizations(
  options?: FetchCorpOrganizationsArgs,
) {
  const response = await get<BaseApiPaginatedResponse<ApiCorpOrganization[]>>(
    '/v2/corp-organizations',
    {
      query: options?.filters,
    },
  );
  return {
    ...response,
    data: response?.data?.map(mapCorpOrganizationFromApi),
  };
}

export async function fetchCorpOrganizationById({
  id,
}: CorpOrganizationByIdArgs) {
  const response = await get<BaseApiResponse<ApiCorpOrganization>>(
    `/v2/corp-organizations/${id}`,
  );
  if (!response?.data) return null;
  return mapCorpOrganizationFromApi(response.data);
}

export async function createCorpOrganization(args: CreateCorpOrganizationArgs) {
  const response = await post<ApiCorpOrganization>(
    '/v2/corp-organizations',
    toApiPayload(args),
  );
  if (!response?.data) return null;
  return mapCorpOrganizationFromApi(response.data);
}

export async function editCorpOrganizationById({
  id,
  ...args
}: EditCorpOrganizationArgs) {
  const response = await patch<ApiCorpOrganization>(
    `/v2/corp-organizations/${id}`,
    toApiPayload(args),
  );
  if (!response?.data) return null;
  return mapCorpOrganizationFromApi(response.data);
}

export async function deleteCorpOrganizationById({
  id,
}: CorpOrganizationByIdArgs) {
  return await destroy(`/v2/corp-organizations/${id}`);
}

export async function freezeCorpOrganization({ id }: CorpOrganizationByIdArgs) {
  const response = await patch<ApiCorpOrganization>(
    `/v2/corp-organizations/${id}/freeze`,
  );
  if (!response?.data) return null;
  return mapCorpOrganizationFromApi(response.data);
}

export async function activateCorpOrganization({
  id,
}: CorpOrganizationByIdArgs) {
  const response = await patch<ApiCorpOrganization>(
    `/v2/corp-organizations/${id}/activate`,
  );
  if (!response?.data) return null;
  return mapCorpOrganizationFromApi(response.data);
}

export async function fetchCorpMembers({
  corpOrganizationId,
  filters,
}: FetchCorpMembersArgs) {
  const response = await get<BaseApiPaginatedResponse<ApiCorpMember[]>>(
    `/v2/corp-organizations/${corpOrganizationId}/members`,
    {
      query: filters,
    },
  );
  return {
    ...response,
    data: response?.data?.map((member) => ({
      id: member.id,
      email: member.email ?? '',
      name: member.name ?? '',
      status: member.status ?? '',
    })),
  };
}

export async function offboardCorpMember({ userId }: OffboardCorpMemberArgs) {
  return patch(`/v2/corp-organizations/members/${userId}/offboard`);
}

export async function updateCorpRebrandTheme({
  corpOrganizationId,
  theme,
}: UpdateCorpRebrandThemeArgs) {
  await put(
    `/v2/corp-organizations/${corpOrganizationId}/rebrand-theme`,
    theme,
  );
}

/**
 * The route hands back `{ data: { url, mediaSource } }` — unwrap it here, the
 * same way `fetchOrganizationSignedProfileUrl` does, so callers get the flat
 * `{ url, mediaSource }` that `useUploadFile` expects.
 */
export async function fetchCorpSignedUploadLogoUrl({
  corpOrganizationId,
  mediaType,
}: FetchCorpSignedUploadLogoUrlArgs) {
  const response = await get<BaseApiResponse<ApiSignedUploadUrl>>(
    `/v2/corp-organizations/${corpOrganizationId}/signed-upload-logo`,
    { query: { mediaType } },
  );
  return response?.data as UiSignedUploadUrl;
}

/**
 * Logo-only PATCH. `editCorpOrganizationById` cannot be reused: its
 * `toApiPayload` always sends the five detail fields, so a logo write would
 * overwrite whatever the details form currently holds.
 */
export async function updateCorpOrganizationLogo({
  corpOrganizationId,
  logoName,
}: UpdateCorpOrganizationLogoArgs) {
  const response = await patch<ApiCorpOrganization>(
    `/v2/corp-organizations/${corpOrganizationId}`,
    { logoName },
  );
  if (!response?.data) return null;
  return mapCorpOrganizationFromApi(response.data);
}
